// API Service for MatchUp
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'matchup_token';

// JWT lives in localStorage so the 30-day session persists across tabs/PWA
// re-opens (sessionStorage would be wiped on tab close).
class ApiService {
  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY);
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  getToken() {
    return this.token || localStorage.getItem(TOKEN_KEY);
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.getToken()) {
      headers['Authorization'] = `Bearer ${this.getToken()}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Une erreur est survenue');
      }

      return data;
    } catch (error) {
      if (error.message === 'Token expiré' || error.message === 'Token invalide') {
        this.setToken(null);
        window.location.reload();
      }
      throw error;
    }
  }

  // Auth
  async register(name, email, password, position = "", city = "", cityLat = null, cityLng = null) {
    const body = { name, email, password, position, city };
    if (cityLat != null && cityLng != null) {
      body.city_lat = cityLat;
      body.city_lng = cityLng;
    }
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    this.setToken(data.token);
    return data;
  }

  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  logout() {
    this.setToken(null);
  }

  // Users
  async getAllUsers() {
    return this.request('/api/users');
  }

  async getUser(userId) {
    return this.request(`/api/users/${userId}`);
  }

  async updateUser(userId, data) {
    return this.request(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateAccountSettings(data) {
    return this.request('/api/user/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async submitFeedback(type, content) {
    return this.request('/api/feedback', {
      method: 'POST',
      body: JSON.stringify({ type, content }),
    });
  }

  async adminListFeedback() {
    return this.request('/api/admin/feedback');
  }

  async uploadPhoto(userId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const resp = await fetch(`${API_URL}/api/users/${userId}/photo`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ detail: 'Upload error' }));
      throw new Error(err.detail || 'Upload failed');
    }
    return resp.json();
  }

  getPhotoUrl(photoPath) {
    if (!photoPath) return null;
    return `${API_URL}/api/photos/${photoPath}`;
  }

  async getHallOfFame() {
    return this.request('/api/users/stats/hall-of-fame');
  }

  // Matches
  async getMatches(status = null) {
    const query = status ? `?status=${status}` : '';
    return this.request(`/api/matches${query}`);
  }

  async getMatch(matchId) {
    return this.request(`/api/matches/${matchId}`);
  }

  async getMatchChat(matchId) {
    return this.request(`/api/matches/${matchId}/chat`);
  }

  async postMatchChat(matchId, message) {
    return this.request(`/api/matches/${matchId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  async createMatch(matchData) {
    return this.request('/api/matches', {
      method: 'POST',
      body: JSON.stringify(matchData),
    });
  }

  async joinMatch(matchId) {
    return this.request(`/api/matches/${matchId}/join`, {
      method: 'POST',
    });
  }

  async leaveMatch(matchId) {
    return this.request(`/api/matches/${matchId}/leave`, {
      method: 'POST',
    });
  }

  async balanceTeams(matchId) {
    return this.request(`/api/matches/${matchId}/balance-teams`, {
      method: 'POST',
    });
  }

  async closeMatch(matchId, data = {}) {
    return this.request(`/api/matches/${matchId}/close`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Votes
  async voteMVP(matchId, votedForUserId) {
    return this.request('/api/votes/mvp', {
      method: 'POST',
      body: JSON.stringify({
        match_id: matchId,
        voted_for_user_id: votedForUserId,
      }),
    });
  }

  async getMatchVotes(matchId) {
    return this.request(`/api/votes/match/${matchId}`);
  }

  // Stats
  async addGoal(userId) {
    return this.request(`/api/users/${userId}/add-goal`, {
      method: 'POST',
    });
  }

  // Ratings
  async ratePlayer(matchId, ratedUserId, score) {
    return this.request('/api/ratings', {
      method: 'POST',
      body: JSON.stringify({ match_id: matchId, rated_user_id: ratedUserId, score }),
    });
  }

  async getMatchRatings(matchId) {
    return this.request(`/api/ratings/match/${matchId}`);
  }

  // Weekly Recap
  async getWeeklyRecap() {
    return this.request('/api/stats/weekly-recap');
  }

  // Terrains
  async terrainsNearby({ lat, lng, radiusKm = 10, limit = 100 }) {
    return this.request(`/api/terrains/nearby?lat=${lat}&lng=${lng}&radius_km=${radiusKm}&limit=${limit}`);
  }

  async terrainsBbox({ minLat, minLng, maxLat, maxLng, limit = 1500 }) {
    return this.request(`/api/terrains/bbox?min_lat=${minLat}&min_lng=${minLng}&max_lat=${maxLat}&max_lng=${maxLng}&limit=${limit}`);
  }

  async terrainsSearch(q, limit = 20) {
    return this.request(`/api/terrains/search?q=${encodeURIComponent(q)}&limit=${limit}`);
  }

  // Badges
  async getAllBadges() {
    return this.request('/api/badges');
  }

  async getUserBadges(userId) {
    return this.request(`/api/users/${userId}/badges`);
  }

  // Admin
  async adminBootstrap(email, bootstrapKey) {
    return this.request('/api/admin/bootstrap', {
      method: 'POST',
      body: JSON.stringify({ email, bootstrap_key: bootstrapKey }),
    });
  }

  async adminGetStats() {
    return this.request('/api/admin/stats');
  }

  async adminListUsers() {
    return this.request('/api/admin/users');
  }

  async adminListMatches() {
    return this.request('/api/admin/matches');
  }

  async adminUpdateUser(userId, updates) {
    return this.request(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async adminImportTerrains() {
    return this.request('/api/admin/import-terrains', { method: 'POST' });
  }

  async adminSearchTerrains(q = '', limit = 30) {
    return this.request(`/api/admin/terrains?q=${encodeURIComponent(q)}&limit=${limit}`);
  }

  async getSettings() {
    return this.request('/api/settings');
  }

  async adminGetSettings() {
    return this.request('/api/admin/settings');
  }

  async adminUpdateSettings(updates) {
    return this.request('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async adminGetPushOverview() {
    return this.request('/api/admin/push-subscriptions');
  }

  async adminPurgePush() {
    return this.request('/api/admin/push-subscriptions', { method: 'DELETE' });
  }

  async adminUpdateTerrain(terrainId, updates) {
    return this.request(`/api/admin/terrains/${terrainId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async adminSeedPaidCenters() {
    return this.request('/api/admin/seed-paid-centers', { method: 'POST' });
  }

  async adminImportForeignTerrains() {
    return this.request('/api/admin/import-foreign-terrains', { method: 'POST' });
  }

  async adminListMissingCities() {
    return this.request('/api/admin/users/cities/missing');
  }

  async adminRepairUserCity(userId, city) {
    return this.request('/api/admin/users/cities/repair', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, city }),
    });
  }

  async adminRepairAllCities() {
    return this.request('/api/admin/users/cities/repair-all', { method: 'POST' });
  }

  async adminDeleteUser(userId) {
    return this.request(`/api/admin/users/${userId}`, { method: 'DELETE' });
  }

  async adminGetGrowth() {
    return this.request('/api/admin/growth');
  }

  async adminSetFlash(text, severity = 'info', durationMinutes = 30) {
    return this.request('/api/admin/flash', {
      method: 'POST',
      body: JSON.stringify({ text, severity, duration_minutes: durationMinutes }),
    });
  }

  async adminClearFlash() {
    return this.request('/api/admin/flash', { method: 'DELETE' });
  }

  async getFlash() {
    return this.request('/api/flash');
  }

  // Push
  async getVapidPublicKey() {
    return this.request('/api/push/vapid-public-key');
  }

  async pushSubscribe(subscription) {
    return this.request('/api/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  async pushTest() {
    return this.request('/api/push/test', { method: 'POST' });
  }

  async pushUnsubscribe() {
    return this.request('/api/notifications/subscribe', { method: 'DELETE' });
  }

  // Google Auth
  async googleAuth(code) {
    const data = await this.request('/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    this.setToken(data.token);
    return data;
  }

  async googleSession(sessionId) {
    return this.request('/api/auth/google-session', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  // Cancel match
  async cancelMatch(matchId) {
    return this.request(`/api/matches/${matchId}/cancel`, { method: 'POST' });
  }

  // Delete match (organizer-only or admin)
  async deleteMatch(matchId) {
    return this.request(`/api/matches/${matchId}`, { method: 'DELETE' });
  }

  // Reports / Moderation
  async createReport(targetUserId, motif, message = '') {
    return this.request('/api/reports', {
      method: 'POST',
      body: JSON.stringify({ target_user_id: targetUserId, motif, message }),
    });
  }

  async adminListReports() {
    return this.request('/api/admin/reports');
  }

  async adminGetAnalytics() {
    return this.request('/api/admin/analytics');
  }

  async geoReverse(lat, lng) {
    return this.request(`/api/geo/reverse?lat=${lat}&lng=${lng}`);
  }

  async geoForward(q, limit = 1) {
    return this.request(`/api/geo/forward?q=${encodeURIComponent(q)}&limit=${limit}`);
  }

  async trackAnonymousVisit(visitorId) {
    return this.request('/api/analytics/visit', {
      method: 'POST',
      body: JSON.stringify({ visitor_id: visitorId }),
    });
  }

  async adminSendWeeklyRecap({ dryRun = false, targetEmail = null } = {}) {
    return this.request('/api/admin/send-weekly-recap', {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun, target_email: targetEmail }),
    });
  }

  // User settings (alerte 15km, etc)
  async updateMySettings(updates) {
    return this.request('/api/users/me/settings', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }
}

export const api = new ApiService();
export default api;
